import { useState, useEffect } from 'react';
import { useTheme } from '../theme';
import { db } from '../dbClient';
import { useToast } from '../Toast';

const ENTITY_TYPES = [
  { value: 'customer', label: '👤 Customer Alerts' },
  { value: 'transaction', label: '💳 Transaction Monitoring' },
  { value: 'sanctions', label: '⚠️ Sanctions Screening' },
  { value: 'pep', label: '🎯 PEP Detection' },
  { value: 'adverse_media', label: '📰 Adverse Media' },
];

const SCENARIO_EXAMPLES = {
  customer: ['PEP_EXPOSURE', 'HIGH_RISK_JURISDICTION', 'COMPLEX_STRUCTURE'],
  transaction: ['HIGH_VALUE_TXN', 'UNUSUAL_PATTERN', 'SANCTIONS_ADJACENT'],
  sanctions: ['DIRECT_HIT', 'FUZZY_MATCH', 'CONSOLIDATED_LIST'],
  pep: ['NEW_PEP', 'PEP_FAMILY', 'PEP_ASSOCIATE'],
  adverse_media: ['CRIMINAL_ACTIVITY', 'REGULATORY_ACTION', 'REPUTATIONAL_RISK'],
};

export default function SLAConfiguration({ company }) {
  const C = useTheme();
  const { showToast } = useToast();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    entity_type: 'customer',
    scenario_type: '',
    response_days_internal: 3,
    response_days_regulatory: 7,
    escalation_days: 1,
    notes: '',
  });

  useEffect(() => {
    loadConfigs();
  }, [company?.id]);

  async function loadConfigs() {
    if (!company?.id) return;
    try {
      setLoading(true);
      const { data, error } = await db
        .from('sla_configurations')
        .select('*')
        .eq('company_id', company.id)
        .order('entity_type', { ascending: true });

      if (error) throw error;
      setConfigs(data || []);
    } catch (err) {
      showToast('Error loading SLA configurations', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    if (!company?.id) {
      showToast('Company not found', 'error');
      return;
    }

    try {
      const payload = {
        ...formData,
        company_id: company.id,
        scenario_type: formData.scenario_type || null,
      };

      let result;
      if (editingId) {
        result = await db
          .from('sla_configurations')
          .update(payload)
          .eq('id', editingId);
      } else {
        result = await db
          .from('sla_configurations')
          .insert([payload]);
      }

      if (result.error) throw result.error;

      showToast(editingId ? 'SLA updated' : 'SLA created', 'success');
      setShowForm(false);
      setEditingId(null);
      setFormData({
        entity_type: 'customer',
        scenario_type: '',
        response_days_internal: 3,
        response_days_regulatory: 7,
        escalation_days: 1,
        notes: '',
      });
      loadConfigs();
    } catch (err) {
      showToast('Error saving SLA configuration', 'error');
      console.error(err);
    }
  }

  function handleEdit(config) {
    setEditingId(config.id);
    setFormData({
      entity_type: config.entity_type,
      scenario_type: config.scenario_type || '',
      response_days_internal: config.response_days_internal,
      response_days_regulatory: config.response_days_regulatory,
      escalation_days: config.escalation_days,
      notes: config.notes || '',
    });
    setShowForm(true);
  }

  async function handleDelete(id) {
    if (!confirm('Delete this SLA configuration?')) return;
    try {
      const { error } = await db
        .from('sla_configurations')
        .delete()
        .eq('id', id);

      if (error) throw error;
      showToast('SLA deleted', 'success');
      loadConfigs();
    } catch (err) {
      showToast('Error deleting SLA configuration', 'error');
      console.error(err);
    }
  }

  const getEntityLabel = (type) => ENTITY_TYPES.find(e => e.value === type)?.label || type;

  if (loading) {
    return (
      <div style={{ padding: '2rem', color: C.textDim }}>Loading SLA configurations...</div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: C.text, marginBottom: '0.5rem', fontFamily: "'Syne', sans-serif" }}>
          SLA Configuration
        </h2>
        <p style={{ color: C.textDim, fontSize: '0.9rem' }}>
          Configure custom SLA thresholds for different alert types. Internal SLA overrides regulatory deadlines.
        </p>
      </div>

      {showForm && (
        <div style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{ color: C.text, marginBottom: '1rem', fontFamily: "'Syne', sans-serif" }}>
            {editingId ? 'Edit SLA' : 'Create New SLA'}
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                Entity Type *
              </label>
              <select
                value={formData.entity_type}
                onChange={(e) => setFormData({...formData, entity_type: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  color: C.text,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '0.9rem'
                }}
              >
                {ENTITY_TYPES.map(e => (
                  <option key={e.value} value={e.value}>{e.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                Scenario Type (optional)
              </label>
              <select
                value={formData.scenario_type}
                onChange={(e) => setFormData({...formData, scenario_type: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  color: C.text,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '0.9rem'
                }}
              >
                <option value="">Default (all scenarios)</option>
                {(SCENARIO_EXAMPLES[formData.entity_type] || []).map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                Internal SLA (days) *
              </label>
              <input
                type="number"
                value={formData.response_days_internal}
                onChange={(e) => setFormData({...formData, response_days_internal: parseInt(e.target.value)})}
                min={1}
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  color: C.text,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '0.9rem'
                }}
              />
              <div style={{ fontSize: '0.75rem', color: C.textDim, marginTop: '0.25rem' }}>Stricter internal deadline</div>
            </div>

            <div>
              <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                Regulatory Deadline (days) *
              </label>
              <input
                type="number"
                value={formData.response_days_regulatory}
                onChange={(e) => setFormData({...formData, response_days_regulatory: parseInt(e.target.value)})}
                min={1}
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  color: C.text,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '0.9rem'
                }}
              />
              <div style={{ fontSize: '0.75rem', color: C.textDim, marginTop: '0.25rem' }}>Regulatory minimum</div>
            </div>

            <div>
              <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                Escalation Days *
              </label>
              <input
                type="number"
                value={formData.escalation_days}
                onChange={(e) => setFormData({...formData, escalation_days: parseInt(e.target.value)})}
                min={0}
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  color: C.text,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: '0.9rem'
                }}
              />
              <div style={{ fontSize: '0.75rem', color: C.textDim, marginTop: '0.25rem' }}>Days before due to escalate</div>
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ color: C.textMid, fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Add any notes about this SLA configuration..."
              style={{
                width: '100%',
                padding: '0.6rem',
                background: C.bg,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                color: C.text,
                fontFamily: "'DM Mono', monospace",
                fontSize: '0.9rem',
                minHeight: '80px',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={handleSave}
              style={{
                padding: '0.6rem 1.2rem',
                background: C.accent,
                color: '#0A0E1A',
                border: 'none',
                borderRadius: 6,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: "'DM Mono', monospace",
                fontSize: '0.85rem'
              }}
            >
              Save
            </button>
            <button
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
                setFormData({
                  entity_type: 'customer',
                  scenario_type: '',
                  response_days_internal: 3,
                  response_days_regulatory: 7,
                  escalation_days: 1,
                  notes: '',
                });
              }}
              style={{
                padding: '0.6rem 1.2rem',
                background: 'transparent',
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                color: C.textMid,
                cursor: 'pointer',
                fontFamily: "'DM Mono', monospace",
                fontSize: '0.85rem'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {!showForm && (
        <button
          onClick={() => setShowForm(true)}
          style={{
            padding: '0.6rem 1.2rem',
            background: `${C.accent}22`,
            border: `1px solid ${C.accent}`,
            color: C.accent,
            borderRadius: 6,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: "'DM Mono', monospace",
            fontSize: '0.85rem',
            marginBottom: '2rem'
          }}
        >
          + New SLA Configuration
        </button>
      )}

      <div style={{
        display: 'grid',
        gap: '1rem'
      }}>
        {configs.length === 0 ? (
          <div style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            padding: '2rem',
            textAlign: 'center',
            color: C.textDim
          }}>
            No SLA configurations yet. Click "New SLA Configuration" to get started.
          </div>
        ) : (
          configs.map(config => (
            <div
              key={config.id}
              style={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 8,
                padding: '1rem'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                <div>
                  <div style={{ color: C.text, fontWeight: 700, fontSize: '0.95rem' }}>
                    {getEntityLabel(config.entity_type)}
                    {config.scenario_type && (
                      <span style={{ color: C.accent, marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                        → {config.scenario_type}
                      </span>
                    )}
                  </div>
                  {config.notes && (
                    <div style={{ color: C.textDim, fontSize: '0.85rem', marginTop: '0.25rem' }}>
                      {config.notes}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleEdit(config)}
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: 'transparent',
                      border: `1px solid ${C.border}`,
                      color: C.text,
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontFamily: "'DM Mono', monospace"
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(config.id)}
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: 'transparent',
                      border: `1px solid #FF6B6B66`,
                      color: '#FF6B6B',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontFamily: "'DM Mono', monospace"
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', fontSize: '0.85rem' }}>
                <div>
                  <div style={{ color: C.textDim, fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.25rem' }}>
                    Internal SLA
                  </div>
                  <div style={{ color: C.accent, fontWeight: 700 }}>{config.response_days_internal} days</div>
                </div>
                <div>
                  <div style={{ color: C.textDim, fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.25rem' }}>
                    Regulatory
                  </div>
                  <div style={{ color: C.steel, fontWeight: 700 }}>{config.response_days_regulatory} days</div>
                </div>
                <div>
                  <div style={{ color: C.textDim, fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 600, marginBottom: '0.25rem' }}>
                    Escalation
                  </div>
                  <div style={{ color: C.text, fontWeight: 700 }}>{config.escalation_days} days before</div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
